import { classifyImplementationName, type HardwareAccelerationKind } from '@playcastdotio/PeerCore';

const UNREACHABLE_COPY =
  'Hardware video encoding could not be reached — streaming in software, which is high-CPU and high-latency. Check GPU drivers and that no other application is holding the hardware encoder.';

export type HardwareCodecWarningBannerProps = {
  encodeKind: HardwareAccelerationKind;
  encoderImplementation?: string;
};

/**
 * Host-beacon-only error shown when hardware encode could not be reached.
 *
 * Red rather than amber because software encode is a real fault, not a nicety — but the
 * session is NOT ended (see SOFTWARE_ENCODE_POLICY.endSessionWhenHardwareUnreachable).
 * Ending it produced sessions that died within seconds on a host whose encoder was
 * available, which is worse for a player than a software stream that runs.
 */
export function HardwareCodecWarningBanner({
  encodeKind,
  encoderImplementation,
}: HardwareCodecWarningBannerProps) {
  // A stale recovery label alone is not evidence of software encoding. Require the
  // selected peer's reported implementation to positively identify a software encoder.
  if (encodeKind !== 'software' || classifyImplementationName(encoderImplementation) !== 'software') {
    return null;
  }

  return (
    <div
      role="alert"
      className="border-red-500/60 bg-red-950/80 text-red-100 mb-3 rounded-md border px-3 py-2 text-sm"
      data-testid="hardware-codec-warning"
      title={encoderImplementation ? `Encoder: ${encoderImplementation}` : undefined}
    >
      {UNREACHABLE_COPY}
    </div>
  );
}
